import {
  addTenantFollowUpItemConverter,
  changeTenantEmailConverter,
  editTenantFollowUpItemConverter,
  getTenantSearchListConvertor,
  markTenantAsKnownConverter,
  transferAnimalOptionsConverter,
  splitTenantConverter,
  animalTransferPreviewConverter,
  tenantConverter,
  tenantDetailsConverter,
  tenantFollowUpItemConverter,
  tenantMaskedDataConverter,
  tenantSplitOptionsConverter,
  transferAnimalBodyConvertor,
} from 'data/convertors/Tenant.convertors';
import {
  TFilter,
  TPageableDataWithContentUi,
  TTableFilters,
} from 'data/types/Table.types';
import {
  TAddTenantFollowUpItemUI,
  TChangeTenantEmailUI,
  TEditTenantFollowUpItemUI,
  TFollowUpItemDB,
  TFollowUpItemUI,
  TMarkTenantAsKnownDB,
  TMarkTenantAsKnownUI,
  TMergeTenantsUI,
  TTransferAnimalOptionsDB,
  TTransferAnimalOptionsUI,
  TSplitTenantUI,
  TAnimalTransferPreviewDB,
  TAnimalTransferPreviewUI,
  TTenantDB,
  TTenantDetailsDB,
  TTenantDetailsUI,
  TTenantMaskedDataDB,
  TTenantMaskedDataUI,
  TTenantSearchListDB,
  TTenantSearchListUI,
  TTenantSplitOptionsDB,
  TTenantSplitOptionsUI,
  TTenantUI,
  TTransferAnimalBodyUI,
} from 'data/types/Tenant.types';
import { ETenantUrls } from 'data/urls/tenant.url';
import { authSplitApi } from '../helpers/slice.helpers';
import { TMarkAsPNCUI } from '../../data/types/Animal.types';
import {
  TAnimalDetailAddDocPacketUI,
  TDocPacketInfoUI,
  TResidentMoveUi,
  TRoiSignature,
} from 'data/types/DocPacket.types';
import {
  animalDetailAddDocPacketConvertor,
  createHPDocInfoConvertor,
  ResidentMoveConverter,
} from 'data/convertors/DocPacket.convertors';

export const tenantApi = authSplitApi('tenant', [
  'tenantSearchList',
  'tenantsList',
  'tenantDetails',
  'transferAnimalOptions',
  'followUpItems',
  'tenantSplitOptions',
]).injectEndpoints({
  endpoints: build => ({
    getTenantSearchList: build.query<TTenantSearchListUI[], TFilter | void>({
      query(params) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.SEARCH}`,
          method: 'GET',
          params: {
            size: 2000,
            ...(params?.search && { search: params.search }),
          },
        };
      },
      providesTags: ['tenantSearchList'],
      transformResponse: (
        data: TPageableDataWithContentUi<TTenantSearchListDB[]>,
      ) => {
        const { content } = data;
        return getTenantSearchListConvertor.fromDb(content);
      },
    }),
    verifyHPDocPacket: build.mutation<
      void,
      { tenantId: string; hpDocumentPacketId: string }
    >({
      query({ tenantId, hpDocumentPacketId }) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.HP_DOC_PAKET}/${ETenantUrls.VERIFY}`,
          method: 'POST',
          body: { tenantId, hpDocumentPacketId },
        };
      },
    }),
    undoVerificationHPDocPacket: build.mutation<
      void,
      { tenantId: string; hpDocumentPacketId: string }
    >({
      query({ tenantId, hpDocumentPacketId }) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.HP_DOC_PAKET}/${ETenantUrls.VERIFY}`,
          method: 'DELETE',
          body: { tenantId, hpDocumentPacketId },
        };
      },
    }),
    turnNotificationOn: build.mutation<void, { tenantId: string }>({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.NOTIFICATIONS}/${ETenantUrls.ENABLE}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['tenantsList', 'tenantDetails'],
    }),
    turnNotificationOff: build.mutation<void, { tenantId: string }>({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.NOTIFICATIONS}/${ETenantUrls.DISABLE}`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['tenantsList', 'tenantDetails'],
    }),
    getTenantsList: build.query<
      TPageableDataWithContentUi<TTenantUI[]>,
      TTableFilters<{ [key: string]: string | string[] }, string | undefined>
    >({
      query({ page, pageSize, filters }) {
        const validFilters = tenantConverter.toDb(filters);
        return {
          url: `${ETenantUrls.TENANT}?page=${page}&size=${pageSize}`,
          method: 'GET',
          params: {
            ...validFilters,
          },
        };
      },
      providesTags: ['tenantsList'],
      transformResponse: (data: TPageableDataWithContentUi<TTenantDB[]>) => {
        return {
          ...data,
          content: data.content.map(item => tenantConverter.fromDb(item)),
        };
      },
    }),
    getTenant: build.query<TTenantDetailsUI, string>({
      query(tenantId: string) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.DETAILS}`,
          method: 'GET',
          params: {
            tenantId,
          },
        };
      },
      providesTags: ['tenantDetails'],
      transformResponse: (data: TTenantDetailsDB) =>
        tenantDetailsConverter.fromDb(data),
    }),
    changeTenantEmail: build.mutation<void, TChangeTenantEmailUI>({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/${data.tenantId}/${ETenantUrls.EMAIL}`,
          method: 'PATCH',
          body: changeTenantEmailConverter.toDb(data),
        };
      },
      invalidatesTags: ['tenantsList', 'tenantDetails'],
    }),
    markAsKnown: build.mutation<TMarkTenantAsKnownDB, TMarkTenantAsKnownUI>({
      query(body: TMarkAsPNCUI) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.TAGS}/${ETenantUrls.KNOWN}`,
          method: 'POST',
          body: markTenantAsKnownConverter.toDb(body),
        };
      },
      invalidatesTags: ['tenantDetails'],
    }),
    removeAsKnown: build.mutation<TMarkTenantAsKnownDB, TMarkTenantAsKnownUI>({
      query(body: TMarkAsPNCUI) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.TAGS}/${ETenantUrls.KNOWN}`,
          method: 'DELETE',
          body: markTenantAsKnownConverter.toDb(body),
        };
      },
      invalidatesTags: ['tenantDetails'],
    }),

    // createAnimalDetailsSADocPacket: build.mutation<void, TAnimalDetailSADocPacketCreateUI>({
    //   query(body) {
    //     return {
    //       url: `${ETenantUrls.TENANT}/${ETenantUrls.HP_DOC}`,
    //       method: 'POST',
    //       body: createAnimalDetailSADocPacketsConvertor.toDb(body),
    //     };
    //   },
    // }),
    createHPDocPacket: build.mutation<string, TAnimalDetailAddDocPacketUI>({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.HP_DOC}`,
          method: 'POST',
          body: animalDetailAddDocPacketConvertor.toDb(body),
        };
      },
    }),
    addHPDocPacketInfo: build.mutation<void, TDocPacketInfoUI>({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.HP_DOC}`,
          method: 'PUT',
          body: createHPDocInfoConvertor.toDb(body),
        };
      },
    }),
    residentMove: build.mutation<void, TResidentMoveUi>({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/${ETenantUrls.MOVE}`,
          method: 'PATCH',
          body: ResidentMoveConverter.toDb(body),
        };
      },
      invalidatesTags: ['tenantDetails'],
    }),

    getTransferAnimalOptions: build.query<
      TTransferAnimalOptionsUI[],
      {
        animalIds: string[];
        sourceTenantResidencyId: string;
        targetTenantResidencyId: string;
      }
    >({
      query({ animalIds, sourceTenantResidencyId, targetTenantResidencyId }) {
        return {
          url: `${ETenantUrls.TENANT}/transfer-animals/options`,
          method: 'GET',
          params: {
            animalIds,
            sourceTenantResidencyId,
            targetTenantResidencyId,
          },
        };
      },
      providesTags: ['transferAnimalOptions'],
      transformResponse: (data: TTransferAnimalOptionsDB) =>
        transferAnimalOptionsConverter.fromDb(data),
    }),

    // Never cached: the preview has to describe the data as it is at the moment the admin confirms.
    getAnimalTransferPreview: build.query<
      TAnimalTransferPreviewUI,
      {
        animalIds: string[];
        sourceTenantResidencyId: string;
        targetTenantResidencyId: string;
        sameResident?: boolean;
      }
    >({
      query({
        animalIds,
        sourceTenantResidencyId,
        targetTenantResidencyId,
        sameResident,
      }) {
        return {
          url: `${ETenantUrls.TENANT}/transfer-animals/preview`,
          method: 'GET',
          params: {
            animalIds,
            sourceTenantResidencyId,
            targetTenantResidencyId,
            sameResident: !!sameResident,
          },
        };
      },
      keepUnusedDataFor: 0,
      transformResponse: (data: TAnimalTransferPreviewDB) =>
        animalTransferPreviewConverter.fromDb(data),
    }),

    transferAnimals: build.mutation<void, TTransferAnimalBodyUI>({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/transfer-animals`,
          method: 'POST',
          body: transferAnimalBodyConvertor.toDb(body),
        };
      },
    }),
    signRoiHdDocPacket: build.mutation<
      void,
      {
        roiSignature: TRoiSignature;
        hpDocumentPacketId: string;
        tenantId: string;
      }
    >({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/hp-doc/sign-roi`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['docPackets', 'docPacket'],
    }),

    tenantDeactivate: build.mutation<
      void,
      { tenantId: string; reason: string }
    >({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/deactivate`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['tenantsList', 'tenantDetails'],
    }),
    tenantReactivate: build.mutation<void, { tenantId: string }>({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/reactivate`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: ['tenantsList', 'tenantDetails'],
    }),
    getTenantMaskedData: build.query<TTenantMaskedDataUI, string>({
      query(tenantId: string) {
        return {
          url: `${ETenantUrls.TENANT}/masked`,
          method: 'GET',
          params: {
            userId: tenantId,
          },
        };
      },
      providesTags: ['tenantDetails'],
      transformResponse: (data: TTenantMaskedDataDB) =>
        tenantMaskedDataConverter.fromDb(data),
    }),

    getTenantFollowUpItems: build.query<
      TFollowUpItemUI[],
      {
        tenantId: string;
        animalId?: string;
        shouldAppendNotificationRelatedItems: boolean;
      }
    >({
      query({ tenantId, animalId, shouldAppendNotificationRelatedItems }) {
        return {
          url: `${ETenantUrls.TENANT}/follow-up-item`,
          method: 'GET',
          params: {
            tenantId,
            animalId,
            shouldAppendNotificationRelatedItems,
          },
        };
      },
      providesTags: ['tenantFollowUpItems'],
      transformResponse: (data: TFollowUpItemDB[]) =>
        (data || []).map(tenantFollowUpItemConverter.fromDb),
    }),

    addTenantFollowUpItem: build.mutation<void, TAddTenantFollowUpItemUI>({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/follow-up-item`,
          method: 'POST',
          body: addTenantFollowUpItemConverter.toDb(data),
        };
      },
      invalidatesTags: ['tenantFollowUpItems', 'tenantDetails'],
    }),

    editTenantFollowUpItem: build.mutation<void, TEditTenantFollowUpItemUI>({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/follow-up-item`,
          method: 'PUT',
          body: editTenantFollowUpItemConverter.toDb(data),
        };
      },
      invalidatesTags: ['tenantFollowUpItems', 'tenantDetails'],
    }),

    deleteTenantFollowUpItem: build.mutation<
      void,
      { tenantId: string; tenantFollowUpItemId: string }
    >({
      query(data) {
        return {
          url: `${ETenantUrls.TENANT}/follow-up-item`,
          method: 'DELETE',
          body: data,
        };
      },
      invalidatesTags: ['tenantFollowUpItems', 'tenantDetails'],
    }),

    mergeTenants: build.mutation<void, TMergeTenantsUI>({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/link`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['tenantsList'],
    }),

    getTenantSplitOptions: build.query<TTenantSplitOptionsUI, string>({
      query(tenantId) {
        return {
          url: `${ETenantUrls.TENANT}/split/options`,
          method: 'GET',
          params: { tenantId },
        };
      },
      providesTags: ['tenantSplitOptions'],
      transformResponse: (data: TTenantSplitOptionsDB) =>
        tenantSplitOptionsConverter.fromDb(data),
    }),

    splitTenant: build.mutation<string, TSplitTenantUI>({
      query(body) {
        return {
          url: `${ETenantUrls.TENANT}/split`,
          method: 'POST',
          body: splitTenantConverter.toDb(body),
          responseHandler: 'text',
        };
      },
      invalidatesTags: [
        'tenantsList',
        'tenantDetails',
        'tenantSearchList',
        'tenantSplitOptions',
      ],
    }),

    undoTenantSplit: build.mutation<string, string>({
      query(splitId) {
        return {
          url: `${ETenantUrls.TENANT}/split/undo`,
          method: 'POST',
          body: { splitId },
          responseHandler: 'text',
        };
      },
      invalidatesTags: [
        'tenantsList',
        'tenantDetails',
        'tenantSearchList',
        'tenantSplitOptions',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTenantSearchListQuery,
  useTurnNotificationOnMutation,
  useTurnNotificationOffMutation,
  useVerifyHPDocPacketMutation,
  useUndoVerificationHPDocPacketMutation,
  useGetTenantsListQuery,
  useGetTenantQuery,
  useChangeTenantEmailMutation,
  useMarkAsKnownMutation,
  useRemoveAsKnownMutation,
  // useCreateAnimalDetailsSADocPacketMutation
  useCreateHPDocPacketMutation,
  useAddHPDocPacketInfoMutation,
  useResidentMoveMutation,

  useGetTransferAnimalOptionsQuery,
  useTransferAnimalsMutation,

  useSignRoiHdDocPacketMutation,
  useTenantDeactivateMutation,
  useTenantReactivateMutation,
  useGetTenantMaskedDataQuery,

  useGetTenantFollowUpItemsQuery,
  useAddTenantFollowUpItemMutation,
  useEditTenantFollowUpItemMutation,
  useDeleteTenantFollowUpItemMutation,
  useLazyGetTenantFollowUpItemsQuery,
  useMergeTenantsMutation,
  useGetTenantSplitOptionsQuery,
  useSplitTenantMutation,
  useUndoTenantSplitMutation,
} = tenantApi;
